/**
 * usePrimeArtifactPropertyForm Hook
 * 
 * Manages all form-related state, handlers, and computed values for the
 * Prime Artifact Property Details Form, including property creation/editing,
 * global property integration, option catalogs, artifact references, and validation.
 */

import { useState, useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import { apiClient } from '../services/apiClient';
import { PrimeArtifactService } from '../services/primeArtifactService';
import { GlobalPropertiesService } from '../services/api/globalPropertiesService';
import { PrimeArtifactState } from '../types/primeModel';

export interface PrimeArtifactProperty {
  id: string;
  prime_artifact_id: string;
  property_key: string;
  property_type: 'string' | 'number' | 'integer' | 'float' | 'boolean' | 'date' | 'date_range' | 'currency' | 'duration' | 'url' | 'uuid' | 'uuid[]' | 'list';
  description?: string;
  is_required: boolean;
  input_source?: 'user_input' | 'system_generated' | 'referenced_artifact' | 'transformed_reference';
  global_option_catalog_ref?: string;
  global_option_catalog_name?: string;
  default_value?: string;
  validation_rules?: Record<string, any>;
  property_scope?: 'artifact' | 'artifact_item';
  is_child_reference?: boolean;
  input_label?: string;
  input_type?: string;
  state_mappings?: Array<{
    id: string;
    state_name: string;
    mapping_type: 'created' | 'updated';
    transition_ref?: string;
    notes?: string;
  }>;
  referenced_artifacts?: Array<{
    id: string;
    referenced_artifact_type_id: string;
    referenced_artifact_type_name?: string;
    artifact_id?: string;
    reference_type?: 'uuid' | 'transformed';
    source_property_key?: string;
    transformation_logic?: any;
    relationship_type?: string;
    notes?: string;
  }>;
  created_at?: string;
  updated_at?: string;
}

interface OptionCatalog {
  id: string;
  name: string;
  description?: string;
  category?: string;
  display_label?: string;
  values?: Array<{
    id?: string;
    value: string;
    display_label?: string;
    description?: string;
    sequence_order?: number;
    is_active?: boolean;
  }>;
}

interface UsePrimeArtifactPropertyFormProps {
  artifactId: string;
  artifactName: string;
  onPropertySaved?: () => void;
  onPropertiesUpdated?: () => void;
}

export const usePrimeArtifactPropertyForm = ({
  artifactId,
  artifactName,
  onPropertySaved,
  onPropertiesUpdated
}: UsePrimeArtifactPropertyFormProps) => {
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PrimeArtifactProperty | null>(null);
  const [optionCatalogs, setOptionCatalogs] = useState<OptionCatalog[]>([]);
  const [artifactTypes, setArtifactTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [artifactStates, setArtifactStates] = useState<PrimeArtifactState[]>([]);
  const [sourceArtifactProperties, setSourceArtifactProperties] = useState<Array<{ property_key: string; property_type: string }>>([]);
  const [sourceArtifactPropertiesMap, setSourceArtifactPropertiesMap] = useState<Record<string, Array<{ property_key: string; property_type: string }>>>({});
  const [selectedSourceArtifactType, setSelectedSourceArtifactType] = useState<string | undefined>(undefined);
  const [sourceArtifacts, setSourceArtifacts] = useState<Array<{ id: string; name: string; artifact_type_id: string }>>([]);
  const [sourceArtifactsLoading, setSourceArtifactsLoading] = useState(false);
  const [sourcePropertiesLoading, setSourcePropertiesLoading] = useState<Record<string, boolean>>({});
  const [showGlobalPropertySelector, setShowGlobalPropertySelector] = useState(false);
  const [showGlobalPropertyModal, setShowGlobalPropertyModal] = useState(false);
  const [selectedGlobalPropertyKey, setSelectedGlobalPropertyKey] = useState<string | null>(null);
  const [globalPropertyExists, setGlobalPropertyExists] = useState<Map<string, boolean>>(new Map());
  const [missingProperties, setMissingProperties] = useState<Record<string, string[]>>({});
  const [propertiesUpdateKey, setPropertiesUpdateKey] = useState(0);

  // Fetch option catalogs
  const fetchOptionCatalogs = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePrimeArtifactPropertyForm] Fetching option catalogs...');
      }
      const response = await apiClient.get('/option-catalogs');
      const catalogs = response.data || [];
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePrimeArtifactPropertyForm] Fetched option catalogs:', catalogs.length);
      }
      setOptionCatalogs(catalogs);
    } catch (error) {
      console.error('[usePrimeArtifactPropertyForm] Error fetching option catalogs:', error);
      setOptionCatalogs([]);
    }
  }, []);

  // Fetch artifact types
  const fetchArtifactTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/artifact-types');
      // Handle both array and object responses
      const types = Array.isArray(response.data) ? response.data : (response.data?.items || response.data?.data || []);
      setArtifactTypes(types);
    } catch (error) {
      console.error('Error fetching artifact types:', error);
      setArtifactTypes([]);
    }
  }, []);

  // Fetch artifact states
  const fetchArtifactStates = useCallback(async () => {
    try {
      const states = await PrimeArtifactService.getPrimeArtifactStates(artifactId);
      setArtifactStates(states || []);
    } catch (error) {
      console.error('Error fetching artifact states:', error);
    }
  }, [artifactId]);

  // Fetch primes — type filter uses server expand (PascalCase UUID ↔ slug-owned rows).
  const fetchSourceArtifacts = useCallback(async (artifactTypeId?: string, searchTerm?: string) => {
    setSourceArtifactsLoading(true);
    try {
      const allArtifacts: any[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams();
        params.set('skip', String(skip));
        params.set('limit', String(limit));
        if (artifactTypeId) {
          // Identity-only artifact_type filter (#18); do not client-filter exact artifact_type_id.
          params.set('artifact_type', artifactTypeId);
        }
        const response = await apiClient.get(`/prime-artifacts/?${params.toString()}`);
        const artifacts = Array.isArray(response.data)
          ? response.data
          : (response.data?.items || response.data?.data || []);
        allArtifacts.push(...artifacts);

        if (artifacts.length < limit) {
          hasMore = false;
        } else {
          skip += limit;
        }
      }

      let artifacts = allArtifacts;

      if (searchTerm && searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        artifacts = artifacts.filter(
          (a: any) =>
            a.name?.toLowerCase().includes(searchLower) ||
            a.description?.toLowerCase().includes(searchLower)
        );
      }

      setSourceArtifacts(artifacts);
    } catch (error) {
      console.error('Error fetching source artifacts:', error);
      setSourceArtifacts([]);
    } finally {
      setSourceArtifactsLoading(false);
    }
  }, []);

  // Fetch properties from a specific artifact (for transformed references)
  const fetchSourceArtifactProperties = useCallback(async (artifactId: string) => {
    if (!artifactId) return [];
    
    setSourcePropertiesLoading(prev => ({ ...prev, [artifactId]: true }));
    try {
      const propsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      const properties = propsResponse.data || [];
      setSourceArtifactPropertiesMap(prev => ({ ...prev, [artifactId]: properties }));
      // Also update the global state for backward compatibility
      setSourceArtifactProperties(properties);
      // Trigger re-render of dependent components
      setPropertiesUpdateKey(prev => prev + 1);
      return properties;
    } catch (error) {
      console.error('Error fetching source artifact properties:', error);
      setSourceArtifactPropertiesMap(prev => ({ ...prev, [artifactId]: [] }));
      setSourceArtifactProperties([]);
      return [];
    } finally {
      setSourcePropertiesLoading(prev => ({ ...prev, [artifactId]: false }));
      // Trigger re-render even on error
      setPropertiesUpdateKey(prev => prev + 1);
    }
  }, []);

  // Check for missing properties in transformation logic
  const checkMissingProperties = useCallback((artifactId: string, transformationLogic: any) => {
    if (!artifactId || !transformationLogic) {
      setMissingProperties(prev => {
        const updated = { ...prev };
        delete updated[artifactId];
        return updated;
      });
      return;
    }
    
    // Use artifact-specific properties if available
    const propertiesRaw = sourceArtifactPropertiesMap[artifactId] || sourceArtifactProperties;
    // Ensure properties is always an array
    const properties = Array.isArray(propertiesRaw) ? propertiesRaw : [];
    const missing: string[] = [];
    
    // Check all properties referenced in transformation logic
    if (transformationLogic.condition?.field) {
      const field = transformationLogic.condition.field;
      if (Array.isArray(properties) && !properties.some(p => p.property_key === field)) {
        missing.push(field);
      }
    }
    
    // Update missing properties state
    if (missing.length > 0) {
      setMissingProperties(prev => ({
        ...prev,
        [artifactId]: missing
      }));
    } else {
      setMissingProperties(prev => {
        const updated = { ...prev };
        delete updated[artifactId];
        return updated;
      });
    }
  }, [sourceArtifactProperties, sourceArtifactPropertiesMap]);

  // Handle adding property (opens global property selector)
  const handleAddProperty = useCallback(() => {
    setShowGlobalPropertySelector(true);
  }, []);

  // Handle property selection from global properties
  const handlePropertySelect = useCallback(async (propertyKeys: string[], includeDependencies: boolean) => {
    try {
      // Create properties for each selected key
      const results = await Promise.allSettled(
        propertyKeys.map(async (propertyKey) => {
          // Fetch global property definition
          const globalProp = await GlobalPropertiesService.getPropertyDefinition(propertyKey);
          
          // Create prime artifact property with data from global property
          await apiClient.post(`/prime-artifacts/${artifactId}/properties`, {
            property_key: propertyKey,
            property_type: globalProp.definition?.property_type || 'string',
            description: globalProp.definition?.description,
            input_source: globalProp.definition?.input_source || 'user_input',
            is_required: globalProp.definition?.validation_rules?.required || false,
            default_value: globalProp.definition?.default_value,
            validation_rules: globalProp.definition?.validation_rules,
            global_option_catalog_ref: globalProp.definition?.global_option_catalog_ref,
            // property_scope is not part of PropertyDefinition, will be auto-determined from state mappings
            // Initialize with empty arrays for state mappings and artifact references
            state_mappings: [],
            referenced_artifacts: []
          });
          
          return propertyKey;
        })
      );
      
      // Count successful and failed operations
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (successful > 0) {
        void message.success(`${successful} propert${successful === 1 ? 'y' : 'ies'} added successfully`);
      }
      
      if (failed > 0) {
        const errors = results
          .filter(r => r.status === 'rejected')
          .map(r => (r as PromiseRejectedResult).reason?.message || 'Unknown error');
        console.error('Failed to add some properties:', errors);
        void message.error(`${failed} propert${failed === 1 ? 'y' : 'ies'} failed to add`);
      }
      
      // Refresh properties list
      onPropertiesUpdated?.();
      setShowGlobalPropertySelector(false);
    } catch (error: any) {
      console.error('Error adding properties:', error);
      void message.error(`Failed to add properties: ${error.message || 'Unknown error'}`);
      throw error; // Re-throw to let GlobalPropertySelector handle it
    }
  }, [artifactId, onPropertiesUpdated]);

  // Handle editing property
  const handleEditProperty = useCallback(async (property: PrimeArtifactProperty) => {
    setEditingProperty(property);
    
    // Extract created and updated states
    const createdStates = property.state_mappings
      ?.filter(m => m.mapping_type === 'created')
      .map(m => m.state_name) || [];
    const updatedStates = property.state_mappings
      ?.filter(m => m.mapping_type === 'updated')
      .map(m => m.state_name) || [];
    
    // Extract artifact references - combine all references (both UUID and transformed with copy_value)
    const allRefs = property.referenced_artifacts?.map(ref => ({
      artifact_type_id: ref.referenced_artifact_type_id,
      artifact_id: ref.artifact_id,
      artifact_type_name: ref.referenced_artifact_type_name,
      source_property_key: ref.source_property_key,
      transformation_logic: ref.transformation_logic,
      relationship_type: ref.relationship_type,
      notes: ref.notes,
      reference_type: ref.reference_type || (!ref.source_property_key && !ref.transformation_logic ? 'uuid' : 'transformed')
    })) || [];
    
    // Separate UUID refs from transformed refs based on input_source
    let uuidRefs: typeof allRefs = [];
    let transformedRefs: typeof allRefs = [];
    
    if (property.input_source === 'referenced_artifact') {
      uuidRefs = allRefs;
      transformedRefs = [];
    } else if (property.input_source === 'transformed_reference') {
      uuidRefs = [];
      transformedRefs = allRefs;
    } else {
      uuidRefs = allRefs.filter(ref => {
        let hasTransformationLogic = false;
        if (ref.transformation_logic) {
          if (typeof ref.transformation_logic === 'object') {
            const keys = Object.keys(ref.transformation_logic);
            hasTransformationLogic = keys.length > 0;
          } else {
            hasTransformationLogic = true;
          }
        }
        return !hasTransformationLogic && (ref.reference_type === 'uuid' || !ref.reference_type);
      });
      
      transformedRefs = allRefs.filter(ref => {
        let hasTransformationLogic = false;
        if (ref.transformation_logic) {
          if (typeof ref.transformation_logic === 'object') {
            const keys = Object.keys(ref.transformation_logic);
            hasTransformationLogic = keys.length > 0;
          } else {
            hasTransformationLogic = true;
          }
        }
        return hasTransformationLogic || ref.reference_type === 'transformed';
      });
    }
    
    // Load artifacts and properties if needed
    if (uuidRefs.length > 0) {
      if (uuidRefs[0].artifact_type_id) {
        await fetchSourceArtifacts(uuidRefs[0].artifact_type_id);
      } else if (uuidRefs[0].artifact_id) {
        await fetchSourceArtifacts();
      }
      if (uuidRefs[0].artifact_id && uuidRefs[0].source_property_key) {
        await fetchSourceArtifactProperties(uuidRefs[0].artifact_id);
      }
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    if (transformedRefs.length > 0) {
      if (transformedRefs[0].artifact_type_id) {
        const typeId = transformedRefs[0].artifact_type_id;
        setSelectedSourceArtifactType(typeId);
        await fetchSourceArtifacts(typeId);
      }
      if (transformedRefs[0].artifact_id) {
        await fetchSourceArtifactProperties(transformedRefs[0].artifact_id);
      }
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    // Prepare form values
    const formValues: any = {
      property_key: property.property_key,
      property_type: property.property_type,
      description: property.description,
      is_required: property.is_required,
      input_source: property.input_source,
      global_option_catalog_ref: property.global_option_catalog_ref,
      default_value: property.default_value,
      validation_rules: property.validation_rules ? JSON.stringify(property.validation_rules, null, 2) : '',
      property_scope: property.property_scope,
      is_child_reference: property.is_child_reference,
      input_label: property.input_label,
      input_type: property.input_type,
      created_in_states: createdStates,
      updated_in_states: updatedStates
    };
    
    // Only populate the appropriate form field based on input_source
    if (property.input_source === 'referenced_artifact') {
      const seenRefs = new Set<string>();
      const uniqueRefs = uuidRefs.filter(ref => {
        const refKey = `${ref.artifact_id}-${ref.source_property_key || ''}`;
        if (seenRefs.has(refKey)) {
          return false;
        }
        seenRefs.add(refKey);
        return true;
      });
      
      formValues.referenced_artifact_types = uniqueRefs.map(ref => ({
        artifact_type_id: ref.artifact_type_id ? String(ref.artifact_type_id) : undefined,
        artifact_id: ref.artifact_id ? String(ref.artifact_id) : undefined,
        source_property_key: ref.source_property_key || undefined,
        relationship_type: ref.relationship_type || undefined,
        notes: ref.notes || undefined
      }));
      formValues.transformed_references = [];
    } else if (property.input_source === 'transformed_reference') {
      formValues.transformed_references = transformedRefs.map(ref => ({
        transformation_logic: ref.transformation_logic,
        artifact_id: ref.artifact_id || null,
        artifact_type_id: ref.artifact_type_id || null,
        source_property_key: ref.source_property_key || null,
        reference_type: 'transformed'
      }));
      formValues.referenced_artifact_types = [];
    } else {
      formValues.referenced_artifact_types = [];
      formValues.transformed_references = [];
    }
    
    // Reset form and set values
    form.resetFields();
    setTimeout(() => {
      form.setFieldsValue(formValues);
      setShowModal(true);
    }, 0);
  }, [form, fetchSourceArtifacts, fetchSourceArtifactProperties]);

  // Handle deleting property
  const handleDeleteProperty = useCallback(async (propertyId: string) => {
    try {
      await apiClient.delete(`/prime-artifact-properties/${propertyId}`);
      void message.success('Property deleted successfully');
      onPropertiesUpdated?.();
    } catch (error: any) {
      void message.error('Failed to delete property: ' + (error.response?.data?.detail || error.message));
    }
  }, [onPropertiesUpdated]);

  // Handle saving property
  const handleSaveProperty = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      // Parse validation rules if provided
      if (values.validation_rules) {
        try {
          values.validation_rules = JSON.parse(values.validation_rules);
        } catch (e) {
          void message.error('Invalid JSON in validation rules');
          return;
        }
      }

      // Extract state mappings
      const stateMappings: Array<{ state_name: string; mapping_type: 'created' | 'updated' }> = [];
      
      if (values.created_in_states && values.created_in_states.length > 0) {
        values.created_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'created' });
        });
      }
      
      if (values.updated_in_states && values.updated_in_states.length > 0) {
        values.updated_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'updated' });
        });
      }

      // Extract artifact references
      const artifactReferences: Array<{ 
        referenced_artifact_type_id: string;
        artifact_id?: string;
        reference_type?: 'uuid' | 'transformed';
        source_property_key?: string;
        transformation_logic?: any;
        relationship_type?: string;
        notes?: string;
      }> = [];
      
      const seenReferences = new Set<string>();
      
      // Process based on input_source
      if (values.input_source === 'referenced_artifact') {
        if (values.referenced_artifact_types && Array.isArray(values.referenced_artifact_types)) {
          const validRefs = values.referenced_artifact_types.filter((ref: any) => ref && ref.artifact_id);
          
          validRefs.forEach((ref: any) => {
            const artifactTypeId = ref.artifact_type_id || 
              sourceArtifacts.find((a: any) => a.id === ref.artifact_id)?.artifact_type_id;
            
            if (!artifactTypeId) {
              console.warn('Skipping reference without artifact_type_id:', ref);
              return;
            }
            
            const refKey = `${ref.artifact_id}-${ref.source_property_key || ''}`;
            
            if (seenReferences.has(refKey)) {
              return;
            }
            seenReferences.add(refKey);
            
            const shouldHaveTransformation = ref.source_property_key && values.input_source === 'referenced_artifact';
            const transformationLogic = shouldHaveTransformation ? {
              type: 'copy_value',
              source_property: ref.source_property_key
            } : undefined;
            
            const refType = ref.source_property_key ? 'transformed' : 'uuid';
            
            artifactReferences.push({
              referenced_artifact_type_id: artifactTypeId,
              artifact_id: ref.artifact_id,
              reference_type: ref.reference_type || refType,
              source_property_key: ref.source_property_key || undefined,
              transformation_logic: transformationLogic,
              relationship_type: ref.relationship_type || undefined,
              notes: ref.notes || undefined
            });
          });
        }
      } else if (values.input_source === 'transformed_reference') {
        if (values.transformed_references && Array.isArray(values.transformed_references)) {
          values.transformed_references.forEach((ref: any) => {
            if (ref && ref.transformation_logic) {
              const calcType = ref.transformation_logic?.type || 'unknown';
              const refKey = `${calcType}-${ref.artifact_id || 'same-artifact'}-${ref.artifact_type_id || 'none'}`;
              
              if (seenReferences.has(refKey)) {
                return;
              }
              seenReferences.add(refKey);
              
              artifactReferences.push({
                referenced_artifact_type_id: ref.artifact_type_id || null,
                reference_type: 'transformed',
                artifact_id: ref.artifact_id || null,
                source_property_key: ref.source_property_key,
                transformation_logic: ref.transformation_logic,
                relationship_type: ref.relationship_type,
                notes: ref.notes
              });
            }
          });
        }
      }

      // Remove the state mapping arrays and artifact references from values
      delete values.created_in_states;
      delete values.updated_in_states;
      delete values.referenced_artifact_types;
      delete values.transformed_references;

      // Backend requires at least one artifact reference
      const finalArtifactReferences = artifactReferences.length > 0 
        ? artifactReferences 
        : [{
            artifact_id: artifactId,
            reference_type: 'self',
            referenced_artifact_type_id: null
          }];

      if (editingProperty) {
        // Update existing property
        await apiClient.put(`/prime-artifacts/${artifactId}/properties/${editingProperty.id}`, {
          ...values,
          state_mappings: stateMappings,
          referenced_artifacts: finalArtifactReferences
        });
        void message.success('Property updated successfully');
      } else {
        // Create new property
        await apiClient.post(`/prime-artifacts/${artifactId}/properties`, {
          ...values,
          state_mappings: stateMappings,
          referenced_artifacts: finalArtifactReferences
        });
        void message.success('Property created successfully');
      }

      setShowModal(false);
      setEditingProperty(null);
      form.resetFields();
      onPropertySaved?.();
      onPropertiesUpdated?.();
    } catch (error: any) {
      console.error('Save property error:', error);
      
      if (error.errorFields && error.errorFields.length > 0) {
        const errorFields = error.errorFields.map((ef: any) => `${ef.name}: ${ef.errors.join(', ')}`).join('; ');
        void message.error('Validation failed: ' + errorFields);
      } else {
        void message.error('Failed to save property: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
      }
    }
  }, [form, editingProperty, artifactId, sourceArtifacts, onPropertySaved, onPropertiesUpdated]);

  // Load initial data
  useEffect(() => {
    void fetchOptionCatalogs();
    void fetchArtifactTypes();
    void fetchArtifactStates();
  }, [fetchOptionCatalogs, fetchArtifactTypes, fetchArtifactStates]);

  return {
    // Form state
    form,
    showModal,
    setShowModal,
    editingProperty,
    setEditingProperty,
    // Data
    optionCatalogs,
    artifactTypes,
    artifactStates,
    sourceArtifacts,
    setSourceArtifacts,
    sourceArtifactsLoading,
    sourceArtifactProperties,
    setSourceArtifactProperties,
    sourceArtifactPropertiesMap,
    sourcePropertiesLoading,
    selectedSourceArtifactType,
    setSelectedSourceArtifactType,
    missingProperties,
    propertiesUpdateKey,
    // Global property integration
    showGlobalPropertySelector,
    setShowGlobalPropertySelector,
    showGlobalPropertyModal,
    setShowGlobalPropertyModal,
    selectedGlobalPropertyKey,
    setSelectedGlobalPropertyKey,
    globalPropertyExists,
    setGlobalPropertyExists,
    // Functions
    fetchOptionCatalogs,
    fetchArtifactTypes,
    fetchArtifactStates,
    fetchSourceArtifacts,
    fetchSourceArtifactProperties,
    checkMissingProperties,
    handleAddProperty,
    handlePropertySelect,
    handleEditProperty,
    handleDeleteProperty,
    handleSaveProperty
  };
};
